import React, { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react'
import { v4 as uuid } from 'uuid'
import clsx from 'clsx'

import './ExpandedTable.scss'

// Context to share table state between compound components
interface ExpandedTableContextValue {
  id: string
  expanded: boolean
  collapsible: boolean
  toggle: () => void
  contentId: string
  headerId: string
  isLoading: boolean
  empty: boolean
  emptyMessage: React.ReactNode | string
}

const ExpandedTableContext = createContext<ExpandedTableContextValue | null>(null)
const useExpandedTableContext = () => {
  const ctx = useContext(ExpandedTableContext)
  if (!ctx) throw new Error('ExpandedTable.* must be used within <ExpandedTable>')
  return ctx
}

type SortDirection = 'asc' | 'desc' | 'none'

type TExpandedTableProps = {
  id?: string
  className?: string
  defaultExpanded?: boolean
  expanded?: boolean
  onToggle?: (expanded: boolean) => void
  collapsible?: boolean
  isLoading?: boolean
  empty?: boolean
  emptyMessage?: React.ReactNode | string
  ariaLabel?: string
  ariaLabelledBy?: string
  children?: React.ReactNode
}

// Forward declarations for compound types
export type ExpandedTableHeader = React.FC<{ children?: React.ReactNode; className?: string }>
export type ExpandedTableTitle = React.FC<{ children?: React.ReactNode; as?: 'label' | 'div' | 'span' }>
export type ExpandedTableActions = React.FC<{ children?: React.ReactNode }>
export type ExpandedTableBody = React.FC<BodyProps>
export type ExpandedTableRow = React.FC<RowProps>
export type ExpandedTableCell = React.FC<CellProps>
export type ExpandedTableCellActions = React.FC<{ children?: React.ReactNode }>

export type ExpandedTableComponent = React.FC<TExpandedTableProps> & {
  Header: ExpandedTableHeader
  Title: ExpandedTableTitle
  Actions: ExpandedTableActions
  Body: ExpandedTableBody
  Row: ExpandedTableRow
  Cell: ExpandedTableCell
  CellActions: ExpandedTableCellActions
}

export type Column = {
  key: string
  header: React.ReactNode
  width?: number | string
  sortable?: boolean
  sortDirection?: SortDirection
  onSort?: (next: SortDirection) => void
  ariaLabel?: string
}

export type BodyProps = {
  className?: string
  columns?: Column[]
  hideHeader?: boolean
  children?: React.ReactNode
  /** Enable simple arrow-key navigation between cells (default: true) */
  keyboardNavigation?: boolean
}

export type RowProps = React.HTMLAttributes<HTMLTableRowElement> & {
  children?: React.ReactNode
}

export type CellProps = React.TdHTMLAttributes<HTMLTableCellElement> & {
  className?: string
  children?: React.ReactNode
  asHeader?: boolean // render as <th> in body rows if needed
}

const ExpandedTable = (props: TExpandedTableProps) => {
  const {
    id,
    className,
    defaultExpanded = true,
    expanded: expandedProp,
    onToggle,
    collapsible = true,
    isLoading = false,
    empty = false,
    emptyMessage = 'No data',
    ariaLabel,
    ariaLabelledBy,
    children
  } = props

  const reactId = uuid()
  const tableId = id ?? `expanded-table-${reactId}`
  const contentId = `${tableId}-content`
  const headerId = `${tableId}-header`

  const [uncontrolledExpanded, setUncontrolledExpanded] = useState(defaultExpanded)
  const isControlled = expandedProp !== undefined
  const expanded = isControlled ? !!expandedProp : uncontrolledExpanded

  const toggle = useCallback(
    (e?: React.MouseEvent) => {
      e?.preventDefault()
      e?.stopPropagation()
      const next = !expanded
      if (!isControlled) setUncontrolledExpanded(next)
      onToggle?.(next)
    },
    [expanded, isControlled, onToggle]
  )

  const ctx: ExpandedTableContextValue = useMemo(
    () => ({ id: tableId, expanded, collapsible, toggle, contentId, headerId, isLoading, empty, emptyMessage }),
    [tableId, expanded, collapsible, toggle, contentId, headerId, isLoading, empty, emptyMessage]
  )

  return (
    <ExpandedTableContext.Provider value={ctx}>
      <div
        className={clsx('tq-expanded-table', className)}
        role="region"
        aria-label={ariaLabel}
        aria-labelledby={ariaLabelledBy ?? headerId}
      >
        {children}
        {/* Body empty/loading fallback when Body isn't provided */}
        {!children && (
          <div id={contentId} className={clsx('tq-expanded-table-content', { 'empty-state': empty })}>
            {isLoading ? (
              <div aria-live="polite" aria-busy="true" className="tq-table-loading">
                <i className="fa fa-spinner fa-spin" aria-hidden="true" /> Loading...
              </div>
            ) : empty ? (
              <p>{emptyMessage}</p>
            ) : null}
          </div>
        )}
      </div>
    </ExpandedTableContext.Provider>
  )
}

const Header = ({ children, className }: { children?: React.ReactNode; className?: string }) => {
  const { collapsible, expanded, toggle, contentId, headerId } = useExpandedTableContext()

  const onKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (!collapsible) return
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault()
        toggle()
      }
    },
    [collapsible, toggle]
  )

  return (
    <div
      id={headerId}
      className={clsx('tq-expanded-table-title', className)}
      role={collapsible ? 'button' : undefined}
      tabIndex={collapsible ? 0 : -1}
      aria-expanded={collapsible ? expanded : undefined}
      aria-controls={collapsible ? contentId : undefined}
      onClick={collapsible ? toggle : undefined}
      onKeyDown={onKeyDown}
    >
      <div className="tq-expanded-table-title-icon" aria-hidden="true">
        <i className={clsx('fa', expanded ? 'fa-chevron-down' : 'fa-chevron-right')} />
      </div>
      {children}
    </div>
  )
}

const Title = ({ children, as = 'label' as const }: { children?: React.ReactNode; as?: 'label' | 'div' | 'span' }) => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const Comp = as as any
  return <Comp className="tq-expanded-table-title-text">{children}</Comp>
}

const Actions = ({ children }: { children?: React.ReactNode }) => {
  return <div className="actions">{children}</div>
}

const Body = ({ className, columns, hideHeader, children, keyboardNavigation = true }: BodyProps) => {
  const { expanded, contentId, isLoading, empty, emptyMessage } = useExpandedTableContext()
  const tableRef = useRef<HTMLTableElement | null>(null)

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (!keyboardNavigation) return
      const target = e.target as HTMLElement
      if (!target || target.getAttribute('data-cell') !== 'true') return
      const td = target.closest('td,th') as HTMLTableCellElement | null
      const tr = td?.parentElement as HTMLTableRowElement | null
      const tbody = tr?.parentElement as HTMLTableSectionElement | null
      if (!td || !tr || !tbody) return
      const rowIndex = Array.from(tbody.querySelectorAll('tr')).indexOf(tr)
      const cells = Array.from(tr.querySelectorAll<HTMLElement>('td[tabindex],th[tabindex]'))
      const colIndex = cells.indexOf(td as HTMLTableCellElement)

      const focusCell = (ri: number, ci: number) => {
        const row = tbody.querySelectorAll<HTMLTableRowElement>('tr')[ri]
        const nextCell = row?.querySelectorAll<HTMLElement>('td[tabindex],th[tabindex]')[ci]
        nextCell?.focus()
      }

      switch (e.key) {
        case 'ArrowRight':
          e.preventDefault()
          if (colIndex < cells.length - 1) focusCell(rowIndex, colIndex + 1)
          break
        case 'ArrowLeft':
          e.preventDefault()
          if (colIndex > 0) focusCell(rowIndex, colIndex - 1)
          break
        case 'ArrowDown':
          e.preventDefault()
          if (tbody && rowIndex < tbody.querySelectorAll('tr').length - 1) focusCell(rowIndex + 1, colIndex)
          break
        case 'ArrowUp':
          e.preventDefault()
          if (rowIndex > 0) focusCell(rowIndex - 1, colIndex)
          break
      }
    },
    [keyboardNavigation]
  )

  return (
    <div
      id={contentId}
      className={clsx('tq-expanded-table-content', className, { 'empty-state': empty })}
      hidden={!expanded}
      aria-hidden={!expanded}
    >
      {isLoading ? (
        <div aria-live="polite" aria-busy="true" className="tq-table-loading">
          <i className="fa fa-spinner fa-spin" aria-hidden="true" /> Loading...
        </div>
      ) : empty ? (
        <p>{emptyMessage ?? 'No data'}</p>
      ) : (
        <div className="tq-table-container">
          <table
            ref={tableRef}
            className="tq-table"
            style={{ '--cols': columns?.length ?? 1 } as React.CSSProperties}
            role="table"
            onKeyDown={handleKeyDown}
          >
            {!hideHeader && columns && columns.length > 0 && (
              <thead>
                <tr>
                  {columns.map(col => (
                    <th key={col.key} role="columnheader" aria-label={col.ariaLabel} style={{ width: col.width }}>
                      {col.sortable ? (
                        <button
                          type="button"
                          className="tq-table-sort-button"
                          aria-label={`Sort ${col.ariaLabel ?? String(col.header)}`}
                          onClick={() => {
                            const next: SortDirection =
                              col.sortDirection === 'asc' ? 'desc' : col.sortDirection === 'desc' ? 'none' : 'asc'
                            col.onSort?.(next)
                          }}
                        >
                          <span>{col.header}</span>
                          {col.sortDirection === 'asc' && <i className="fa fa-sort-asc" aria-hidden="true" />}
                          {col.sortDirection === 'desc' && <i className="fa fa-sort-desc" aria-hidden="true" />}
                          {!col.sortDirection && <i className="fa fa-sort" aria-hidden="true" />}
                        </button>
                      ) : (
                        col.header
                      )}
                    </th>
                  ))}
                </tr>
              </thead>
            )}
            <tbody>{children}</tbody>
          </table>
        </div>
      )}
    </div>
  )
}

const Row = ({ children, className, ...rest }: RowProps) => {
  return (
    <tr className={className} {...rest}>
      {children}
    </tr>
  )
}

const Cell = ({ children, asHeader = false, className, ...rest }: CellProps) => {
  const commonProps = {
    className,
    tabIndex: 0,
    'data-cell': 'true'
  } as const

  if (asHeader) {
    return (
      <th role="columnheader" {...(rest as React.ThHTMLAttributes<HTMLTableCellElement>)} {...commonProps}>
        {children}
      </th>
    )
  }

  return (
    <td role="cell" {...rest} {...commonProps}>
      {children}
    </td>
  )
}

const CellActions = ({ children }: { children?: React.ReactNode }) => {
  return <div className="actions">{children}</div>
}

const CompoundExpandedTable = ExpandedTable as ExpandedTableComponent
CompoundExpandedTable.Header = Header
CompoundExpandedTable.Title = Title
CompoundExpandedTable.Actions = Actions
CompoundExpandedTable.Body = Body
CompoundExpandedTable.Row = Row
CompoundExpandedTable.Cell = Cell
CompoundExpandedTable.CellActions = CellActions

export default CompoundExpandedTable
